import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminAxios } from "@/lib/adminAuth";
import { useAdmin } from "@/context/AdminContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, Loader2, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  nouveau: { label: "Nouveau", active: "bg-slate-700 text-white", idle: "bg-slate-100 text-slate-600 hover:bg-slate-200" },
  en_cours: { label: "En cours", active: "bg-amber-500 text-white", idle: "bg-slate-100 text-slate-600 hover:bg-slate-200" },
  traite: { label: "Traité", active: "bg-emerald-500 text-white", idle: "bg-slate-100 text-slate-600 hover:bg-slate-200" },
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
    <div className="text-sm text-slate-900">{value || <span className="text-slate-300">—</span>}</div>
  </div>
);

const AdminQuoteDetailPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { updateQuoteStatus, deleteQuote } = useAdmin();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [status, setStatus] = useState("nouveau");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await adminAxios().get(`/admin/quotes/${id}`);
        setQuote(res.data);
        setNotes(res.data.notes || "");
        setStatus(res.data.status || "nouveau");
      } catch {
        toast.error("Devis introuvable.");
        nav("/admin/devis");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, nav]);

  const handleStatus = async (s) => {
    setStatus(s);
    await updateQuoteStatus(id, s);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await adminAxios().patch(`/admin/quotes/${id}/notes`, { notes });
      toast.success("Notes enregistrées.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
    </div>
  );
  if (!quote) return null;

  const d = quote.data || {};
  const clientName = d.name || `${d.first_name || ""} ${d.last_name || ""}`.trim();

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <button onClick={() => nav("/admin/devis")} className="flex items-center gap-1 hover:text-slate-900 transition-colors">
          <ChevronLeft className="h-4 w-4" /> Devis
        </button>
        <span>/</span>
        <span className="text-slate-900 font-medium">#{id.slice(0, 8)}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-extrabold text-2xl text-slate-900">{clientName}</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Reçu le {new Date(quote.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="flex gap-2">
          {d.email && (
            <a href={`mailto:${d.email}?subject=Votre demande de devis BIONET SOLUTIONS`}
               className="flex items-center gap-1.5 text-sm text-brand-blue hover:underline">
              <Mail className="h-4 w-4" /> Email
            </a>
          )}
          {d.phone && (
            <a href={`tel:${d.phone}`} className="flex items-center gap-1.5 text-sm text-brand-green hover:underline">
              <Phone className="h-4 w-4" /> Appeler
            </a>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <p className="font-semibold text-slate-900 text-sm">Informations client</p>
          <Field label="Nom" value={clientName} />
          <Field label="Email" value={d.email && <a href={`mailto:${d.email}`} className="text-brand-blue hover:underline">{d.email}</a>} />
          <Field label="Téléphone" value={d.phone && <a href={`tel:${d.phone}`} className="text-brand-blue hover:underline">{d.phone}</a>} />
          <Field label="Ville" value={d.city} />
          <Field label="Code postal" value={d.postal_code} />
          <Field label="Langue" value={d.language === "en" ? "Anglais" : "Français"} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <p className="font-semibold text-slate-900 text-sm">Détails prestation</p>
          <Field label="Type client" value={d.client_type} />
          <Field label="Catégorie service" value={d.service_category} />
          <Field label="Surface" value={d.surface && `${d.surface} m²`} />
          <Field label="Encrassement" value={d.dirtiness} />
          <Field label="Urgence" value={d.urgency} />
          {d.description && <Field label="Description" value={d.description} />}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Estimation</p>
            <p className="text-2xl font-extrabold text-brand-green">
              {quote.estimate_min && quote.estimate_max
                ? `${Math.round(quote.estimate_min)} – ${Math.round(quote.estimate_max)} €`
                : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Statut */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="font-semibold text-slate-900 text-sm mb-3">Statut du devis</p>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(STATUS_META).map(([s, meta]) => (
            <button
              key={s}
              onClick={() => handleStatus(s)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${status === s ? meta.active : meta.idle}`}
              data-testid={`status-btn-${s}`}
            >
              {meta.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <p className="font-semibold text-slate-900 text-sm">Notes internes</p>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ajoutez vos notes ici…"
          rows={4}
          data-testid="quote-notes"
        />
        <Button
          onClick={saveNotes}
          disabled={savingNotes}
          className="rounded-full bg-brand-green hover:bg-brand-green/90"
          data-testid="save-notes"
        >
          {savingNotes && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Enregistrer les notes
        </Button>
      </div>

      {/* Danger zone */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          className="rounded-full border-red-200 text-red-500 hover:bg-red-50"
          onClick={() => setConfirmDelete(true)}
          data-testid="delete-quote-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer ce devis
        </Button>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ce devis ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Cette action est irréversible.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDelete(false)}>Annuler</Button>
            <Button
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
              onClick={async () => { await deleteQuote(id); nav("/admin/devis"); }}
              data-testid="confirm-delete-quote-detail"
            >
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuoteDetailPage;
